import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { BadgeCheck, RefreshCw, QrCode, EyeOff } from 'lucide-react';
import './MembershipCard.css';

/**
 * MembershipCard — the member's live club card, top of the Wallet (v35).
 *
 * Identity only: club badge, name, "Member · 2026/27 season ·
 * valid to 31 Aug", a QR that rotates every ~60s with a countdown ring, and
 * the short code under it for the typed fallback. No photo, no perks text.
 *
 * Renders NOTHING for non-holders and for clubs with cards switched off —
 * no teaser, no locked card (spec rule).
 *
 * Rotation: GET /membership/card mints a fresh single-use token each time.
 * We re-fetch a few seconds before the token's own expiry (so the screen
 * never shows a dead QR) and whenever the page comes back into view — the
 * common case is a phone woken at the gate after sitting in a pocket.
 */
const REFRESH_LEAD_MS = 4000;
const MIN_REFETCH_WAIT_MS = 15000; // a wrong phone clock must never turn into a token storm


/**
 * `hidden` (Stuart, 16 Sept): while a sponsor offer code is expanded lower in
 * the Wallet, the card folds to its header so there is only ever ONE QR on
 * screen — a volunteer can't catch the wrong one. Rotation pauses while
 * hidden (no tokens minted for nothing); `onShow` closes the offer and the
 * card refetches a fresh token as it unfolds.
 */
export default function MembershipCard({ clubSlug, club, hidden = false, onShow }) {
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);

  const { data, refetch, isFetching, isError, dataUpdatedAt } = useQuery({
    queryKey: ['membership-card', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/membership/card`).then(r => r.data),
    enabled: !!clubSlug,
    staleTime: 0,
    refetchOnWindowFocus: false, // the visibilitychange handler below covers it (one token per wake, not two)
    retry: 1,
  });

  const holder = !!data?.holder;

  // Expiry is anchored to when THIS phone received the token (dataUpdatedAt),
  // not to the server's clock — a phone a few seconds fast or slow must not
  // show a dead QR as live, or refetch in a loop (QA #2).
  const ttlMs = (data?.ttl_seconds || 60) * 1000;
  const expiresAt = holder && dataUpdatedAt ? dataUpdatedAt + ttlMs : 0;

  // Tick the ring once a second; schedule the refetch just before expiry.
  useEffect(() => {
    if (!holder || !expiresAt || hidden) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const wait = Math.max(expiresAt - Date.now() - REFRESH_LEAD_MS, MIN_REFETCH_WAIT_MS);
    timerRef.current = setTimeout(() => { refetch(); }, wait);
    return () => { clearInterval(tick); clearTimeout(timerRef.current); };
  }, [holder, expiresAt, refetch, hidden]);

  // Unfolding after a hide → the token on screen may be minutes old.
  const wasHiddenRef = useRef(hidden);
  useEffect(() => {
    if (wasHiddenRef.current && !hidden && holder) { setNow(Date.now()); refetch(); }
    wasHiddenRef.current = hidden;
  }, [hidden, holder, refetch]);

  // Phone woken / app resumed → the token on screen is almost certainly dead.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible' && !hidden) refetch(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refetch, hidden]);

  if (!data) return null;
  // B-66: Marakin staff on the implicit platform membership get a PREVIEW of
  // the card (server sends `staff_preview` alongside holder:false). Static:
  // no rotation, no token — the QR is a fixed string every scanner refuses.
  if (!holder && data.staff_preview) {
    return <PreviewCard preview={data.staff_preview} club={club} hidden={hidden} onShow={onShow} />;
  }
  if (!holder) return null;

  const remainingMs = Math.max(0, expiresAt - now);
  const fraction = Math.min(1, remainingMs / ttlMs);
  const secondsLeft = Math.ceil(remainingMs / 1000);
  const stale = remainingMs <= 0;

  // SVG ring: circumference of r=22 → 138.2
  const C = 138.2;
  const dash = C * fraction;

  // Marakin palette only (Stuart, 16 Sept — MC-7): GOLD card, charcoal ink,
  // green accent for the kicker + countdown ring. Club identity is the badge
  // + name, not the colour scheme (club-colour theming is locked away, W-3).
  const primary = '#C4943A';
  const accent = '#1E4A35';
  const ink = '#2C2B28';
  const validTo = data.valid_until
    ? new Date(`${data.valid_until}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <section className="mcard" style={{ '--mcard-primary': primary, '--mcard-accent': accent, '--mcard-ink': ink }} data-tour="wallet-card" aria-label="Club membership card">
      <div className="mcard-top">
        <div className="mcard-badge">
          {club?.logo_url
            ? <img src={club.logo_url} alt="" />
            : <span>{(club?.name || data.club_name || '?').charAt(0)}</span>}
        </div>
        <div className="mcard-club">
          <div className="mcard-club-name">{club?.name || data.club_name}</div>
          <div className="mcard-kicker"><BadgeCheck size={13} /> Club member{data.season_label ? ` · ${data.season_label} season` : ''}</div>
        </div>
      </div>

      <div className="mcard-name">{data.first_name} {data.last_name}</div>
      {validTo && <div className="mcard-valid">Valid to {validTo}</div>}

      {hidden ? (
        <button type="button" className="mcard-show" onClick={onShow}>
          <QrCode size={16} /> Show my card
          <span>Hidden while a sponsor code is open — one QR on screen at a time</span>
        </button>
      ) : (
      <>
      <div className={`mcard-qr-wrap ${stale ? 'is-stale' : ''}`}>
        <img className="mcard-qr" src={data.qr_image} alt="Your membership QR code" />
        <div className="mcard-ring" aria-hidden="true">
          <svg viewBox="0 0 48 48">
            <circle className="mcard-ring-track" cx="24" cy="24" r="22" />
            <circle className="mcard-ring-fill" cx="24" cy="24" r="22"
              strokeDasharray={`${dash} ${C}`} />
          </svg>
          <span className="mcard-ring-num">{stale ? <RefreshCw size={12} className={isFetching ? 'spin-slow' : ''} /> : secondsLeft}</span>
        </div>
      </div>
      <div className="mcard-code">{data.code}</div>
      <p className="mcard-hint">
        {isError ? 'Couldn\'t refresh — check your connection.' : 'Show this at the gate or the bar. It refreshes itself every minute.'}
      </p>
      </>
      )}
    </section>
  );
}

/**
 * B-66 — the staff preview card. Same gold card so staff can show people
 * what members see, but labelled on its face, dashed, with no countdown and
 * "STAFF PREVIEW" where the typed code would be. No data-tour anchor: tours
 * never run on implicit memberships (GuidedTour.jsx).
 */
function PreviewCard({ preview, club, hidden, onShow }) {
  const validTo = preview.valid_until
    ? new Date(`${preview.valid_until}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  return (
    <section className="mcard mcard-preview" style={{ '--mcard-primary': '#C4943A', '--mcard-accent': '#1E4A35', '--mcard-ink': '#2C2B28' }} aria-label="Staff preview of the club membership card">
      <div className="mcard-preview-label"><EyeOff size={13} /> {preview.label}</div>
      <div className="mcard-top">
        <div className="mcard-badge">
          {club?.logo_url
            ? <img src={club.logo_url} alt="" />
            : <span>{(club?.name || preview.club_name || '?').charAt(0)}</span>}
        </div>
        <div className="mcard-club">
          <div className="mcard-club-name">{club?.name || preview.club_name}</div>
          <div className="mcard-kicker"><BadgeCheck size={13} /> Club member{preview.season_label ? ` · ${preview.season_label} season` : ''}</div>
        </div>
      </div>

      <div className="mcard-name">{preview.first_name} {preview.last_name}</div>
      {validTo && <div className="mcard-valid">Valid to {validTo}</div>}

      {hidden ? (
        <button type="button" className="mcard-show" onClick={onShow}>
          <QrCode size={16} /> Show the preview card
          <span>Hidden while a sponsor code is open — one QR on screen at a time</span>
        </button>
      ) : (
        <>
          <div className="mcard-qr-wrap mcard-qr-preview">
            <img className="mcard-qr" src={preview.qr_image} alt="Staff preview QR code — not valid" />
          </div>
          <div className="mcard-code">STAFF PREVIEW</div>
          <p className="mcard-hint">This is how a club member's card looks. Scanning it is refused and nothing is recorded.</p>
        </>
      )}
    </section>
  );
}
